import { Helmet } from "react-helmet-async";
import {
  Apple,
  Play,
  Target,
  Megaphone,
  Video,
  Users,
  LineChart,
  Sparkles,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import Stats from "../components/homepage/Stats";
import PageHero, {
  SectionHeader,
  FeatureGrid,
  NumberedStrip,
} from "../components/shared/PageHero";

const CHANNELS = [
  { icon: Apple, title: "Apple Search Ads", text: "iOS intent traffic at the moment of search. Low-CPI keyword bidding + creative sets." },
  { icon: Play, title: "Google UAC", text: "AI-driven App campaigns across Search, Play, YouTube and Display — tuned for install + in-app actions." },
  { icon: Target, title: "Meta App Install", text: "Facebook & Instagram advantage+ campaigns with creative testing velocity." },
  { icon: Video, title: "TikTok for Business", text: "UGC-style video ads, Spark Ads, and Smart Performance Campaigns." },
  { icon: Users, title: "Influencer Marketing", text: "Creator partnerships that convert — not just vanity reach." },
  { icon: LineChart, title: "Incrementality Testing", text: "MMM, geo-lift, and conversion value modeling so you spend where it actually works." },
];

const PROCESS = [
  { title: "Benchmark", text: "Audit current channels, CPI, D7 retention and LTV." },
  { title: "Creative Lab", text: "10–20 variants per channel, batch-tested weekly." },
  { title: "Channel Mix", text: "Optimize across ASA, UAC, Meta, TikTok — paired with ASO organic." },
  { title: "Scale", text: "Double down on winners. Expand to secondary markets." },
  { title: "LTV Loop", text: "Predictive LTV models feed back into creative and bidding." },
];

export default function UserAcquisitionPage() {
  return (
    <>
      <Helmet>
        <title>
          App User Acquisition Services — Apple Search Ads, UAC, Meta, TikTok | Lumotis
        </title>
        <meta
          name="description"
          content="Lumotis's User Acquisition service runs Apple Search Ads, Google UAC, Meta and TikTok app install campaigns — paired with ASO so paid traffic actually converts."
        />
        <link rel="canonical" href="https://lumotis.com/user-acquisition" />
      </Helmet>
      <Navbar />
      <main data-testid="ua-page">
        <PageHero
          testid="ua-hero"
          eyebrow="User Acquisition"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "User Acquisition" }]}
          title="Paid app growth that compounds on top of"
          titleAccent="ranked organic."
          subtitle="Apple Search Ads, Google UAC, Meta and TikTok campaigns — engineered by the same team that runs your ASO. Lower CPI. Higher LTV. Real incrementality."
          primaryCta={{ to: "/contact", label: "Plan my UA strategy", testid: "ua-cta-primary" }}
          secondaryCta={{ to: "/#case-studies", label: "See results", testid: "ua-cta-secondary" }}
          rightSlot={
            <div className="bg-zinc-50 border border-zinc-200 p-8 shadow-xl rounded-2xl">
              <p className="text-overline text-electric mb-4">Typical 90-day lift</p>
              <dl className="grid grid-cols-2 gap-y-6 gap-x-4">
                <div>
                  <dt className="text-xs uppercase tracking-widest text-zinc-500">CPI</dt>
                  <dd className="font-display text-3xl font-black text-charcoal mt-1">−42%</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-zinc-500">ROAS D30</dt>
                  <dd className="font-display text-3xl font-black text-electric mt-1">+118%</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-zinc-500">Install Volume</dt>
                  <dd className="font-display text-3xl font-black text-charcoal mt-1">+267%</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-zinc-500">D7 Retention</dt>
                  <dd className="font-display text-3xl font-black text-electric mt-1">+24%</dd>
                </div>
              </dl>
            </div>
          }
        />

        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Channels"
              title="Every paid channel that moves app installs."
              description="Run by operators who've spent millions on each platform."
            />
            <FeatureGrid items={CHANNELS} testidPrefix="ua-channel" />
          </div>
        </section>

        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-50 pointer-events-none" />
          <div className="container relative">
            <div className="max-w-3xl mb-14">
              <p className="text-overline text-electric mb-3">02 — Process</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-balance">
                A creative-first, data-honest UA engine.
              </h2>
            </div>
            <NumberedStrip steps={PROCESS} testidPrefix="ua-step" />
          </div>
        </section>

        <section className="py-20 md:py-28 bg-white">
          <div className="container grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-overline text-electric mb-3">03 — Philosophy</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
                Paid UA without ASO is a leaking bucket.
              </h2>
              <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
                Every dollar of paid traffic flows through your App Store
                listing. If the listing converts at 18% instead of 34%, you're
                paying 2x for the same installs. That's why our UA team and ASO
                team share a Slack channel — not just a project management tool.
              </p>
            </div>
            <div className="lg:col-span-5 bg-zinc-50 border border-zinc-200 p-8">
              <Sparkles className="w-8 h-8 text-electric mb-4" />
              <p className="font-display text-xl font-bold text-charcoal leading-snug">
                “We cut CPI 42% in 60 days — not by bidding smarter, but by
                letting Lumotis rebuild our App Store page first.”
              </p>
              <p className="mt-4 text-sm text-zinc-500">
                — Performance Lead, UAE Gaming Studio
              </p>
            </div>
          </div>
        </section>

        <Stats />
        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
